import { useRef, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Menu, Search, ShieldCheck, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/Avatar'
import { Logo } from '@/components/Logo'
import { Badge } from '@/components/Badge'
import { IconButton } from '@/components/Button'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { useAuth } from '@/features/auth/AuthContext'
import { cn } from '@/lib/utils'

const NAV = [
  { to: '/disciplines', key: 'nav.disciplines' },
  { to: '/catalog', key: 'nav.catalog' },
  { to: '/me', key: 'nav.myLibrary' },
] as const

function navClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded-sm px-3 py-2 text-body transition-colors duration-fast hover:text-text',
    isActive ? 'font-semibold text-text-link' : 'text-text-body',
  )
}

export function Header() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { profile, hasRole, signOut, setPreferredLanguage } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  function submitSearch(event: React.FormEvent): void {
    event.preventDefault()
    const trimmed = query.trim()
    navigate(trimmed ? `/catalog?q=${encodeURIComponent(trimmed)}` : '/catalog')
    setMobileOpen(false)
  }

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-content items-center gap-4 px-4 py-3">
        {/* The lockup: the ITPU wordmark, a hairline, then the product name. */}
        <Link to="/" className="flex shrink-0 items-center gap-3 rounded-sm py-1">
          <Logo size={26} />
          <span aria-hidden className="hidden h-6 w-px bg-border sm:block" />
          <span className="hidden text-body font-semibold text-text sm:inline">
            {t('nav.library')}
          </span>
          <span className="sr-only">{t('app.name')}</span>
        </Link>

        <nav aria-label={t('nav.mainNavigation')} className="hidden shrink-0 items-center gap-1 md:flex">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={navClass}>
              {t(item.key)}
            </NavLink>
          ))}
          {hasRole('admin') && (
            <NavLink to="/admin" className={navClass}>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck aria-hidden className="size-4" strokeWidth={1.75} />
                {t('nav.admin')}
              </span>
            </NavLink>
          )}
        </nav>

        <form role="search" onSubmit={submitSearch} className="ml-auto hidden min-w-[220px] flex-1 lg:block">
          <label htmlFor="header-search" className="sr-only">
            {t('search.label')}
          </label>
          <div className="relative ml-auto w-full max-w-[320px]">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
              strokeWidth={1.75}
            />
            <input
              id="header-search"
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="w-full rounded-sm border border-border bg-surface py-[7px] pl-9 pr-3 text-body transition-colors duration-fast focus:border-itpu-teal"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <LanguageSwitcher
            className="hidden md:inline-flex"
            onChange={(language) => void setPreferredLanguage(language)}
          />
          <ThemeToggle className="hidden lg:inline-flex" />

          {profile && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label={t('nav.userMenu')}
                  className="rounded-pill focus-visible:outline-offset-4"
                >
                  <Avatar displayName={profile.displayName} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 w-64 rounded-md border border-border bg-surface p-2 shadow-e2"
                >
                  <div className="border-b border-border px-2 pb-2">
                    <p className="truncate text-body font-medium">{profile.displayName}</p>
                    <p className="truncate text-meta text-text-muted">{profile.email}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {profile.roles.map((role) => (
                        <Badge
                          key={role}
                          tone={role === 'teacher' ? 'faculty' : role === 'admin' ? 'brand' : 'neutral'}
                        >
                          {t(`roles.${role}`)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <DropdownMenu.Item asChild>
                    <Link
                      to="/me"
                      className="block cursor-pointer rounded-sm px-2 py-2 text-body outline-none data-[highlighted]:bg-surface-tint"
                    >
                      {t('nav.myLibrary')}
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item asChild>
                    <Link
                      to="/about"
                      className="block cursor-pointer rounded-sm px-2 py-2 text-body outline-none data-[highlighted]:bg-surface-tint"
                    >
                      {t('nav.about')}
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="my-1 h-px bg-border" />
                  <div className="flex items-center justify-between gap-2 px-2 py-1 lg:hidden">
                    <LanguageSwitcher onChange={(language) => void setPreferredLanguage(language)} />
                    <ThemeToggle />
                  </div>
                  <DropdownMenu.Item
                    onSelect={() => void signOut()}
                    className="cursor-pointer rounded-sm px-2 py-2 text-body outline-none data-[highlighted]:bg-surface-tint"
                  >
                    {t('nav.signOut')}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}

          <IconButton
            label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            className="md:hidden"
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </IconButton>
        </div>
      </div>

      {mobileOpen && (
        <nav
          aria-label={t('nav.mainNavigation')}
          className="border-t border-border px-4 py-3 md:hidden"
        >
          <form role="search" onSubmit={submitSearch} className="mb-3">
            <label htmlFor="mobile-search" className="sr-only">
              {t('search.label')}
            </label>
            <input
              id="mobile-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="w-full rounded-sm border border-border bg-surface px-4 py-2 text-body"
            />
          </form>
          <ul className="flex flex-col gap-1">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={navClass} onClick={() => setMobileOpen(false)}>
                  {t(item.key)}
                </NavLink>
              </li>
            ))}
            {hasRole('admin') && (
              <li>
                <NavLink to="/admin" className={navClass} onClick={() => setMobileOpen(false)}>
                  {t('nav.admin')}
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
      )}
    </header>
  )
}
